import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet, Plus, Minus, Trash2, Star, StarOff, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fmt } from "@/lib/format";
import { formatApiError } from "@/lib/errors";

export default function AdminInvestors() {
  const [list, setList] = useState([]);
  const [walletUser, setWalletUser] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [mode, setMode] = useState(null); // 'credit' | 'debit' | null
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/investors").then(({ data }) => setList(data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const openWallet = async (u) => {
    setWalletUser(u);
    setWalletData(null);
    setMode(null);
    setAmount("");
    setNote("");
    try {
      const { data } = await api.get(`/admin/users/${u.id}/wallet`);
      setWalletData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load wallet"));
    }
  };

  const submitAdjust = async () => {
    if (!walletUser || !mode) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${walletUser.id}/wallet/${mode}`, {
        amount: amt,
        note: note || undefined,
      });
      toast.success(`${mode === "credit" ? "Credited" : "Debited"} ${fmt(amt)}`);
      // Refresh wallet detail + outer list
      const fresh = await api.get(`/admin/users/${walletUser.id}/wallet`);
      setWalletData(fresh.data);
      setMode(null);
      setAmount("");
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Operation failed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleAffiliate = async (u) => {
    const enable = !u.is_affiliate;
    const label = enable ? "affiliate/enable" : "affiliate/disable";
    if (!confirm(enable ? `Enable affiliate broker for ${u.name}? They'll earn ₦20,000 per brick sold via their referral link.` : `Disable affiliate status for ${u.name}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/${label}`);
      toast.success(enable ? "Affiliate enabled" : "Affiliate disabled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed"));
    }
  };

  const deleteUser = async (u) => {
    if (!confirm(`Delete ${u.name} (${u.email})? This cannot be undone. Users who own bricks cannot be deleted.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to delete"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">People</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-8">
        Investors
      </h1>

      <div className="bp-surface rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="overline">Name</TableHead>
              <TableHead className="overline">Email</TableHead>
              <TableHead className="overline">KYC</TableHead>
              <TableHead className="overline">Role</TableHead>
              <TableHead className="overline">Bricks</TableHead>
              <TableHead className="overline">Invested</TableHead>
              <TableHead className="overline">Wallet</TableHead>
              <TableHead className="overline">Joined</TableHead>
              <TableHead className="overline text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                  No investors yet
                </TableCell>
              </TableRow>
            ) : (
              list.map((u) => (
                <TableRow key={u.id} className="border-white/5" data-testid={`investor-${u.id}`}>
                  <TableCell className="text-white">{u.name}</TableCell>
                  <TableCell className="text-gray-400 text-sm">{u.email}</TableCell>
                  <TableCell><KycPill status={u.kyc_status || "none"} /></TableCell>
                  <TableCell>
                    {u.is_affiliate ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 inline-flex items-center gap-1" data-testid={`affiliate-badge-${u.id}`}>
                        <Star className="w-3 h-3" /> Affiliate
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest">Investor</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-white">{u.bricks_owned || 0}</TableCell>
                  <TableCell className="font-mono text-[#D4AF37]">{fmt(u.total_invested || 0, { decimals: true })}</TableCell>
                  <TableCell className="font-mono text-gray-300">{fmt(u.wallet_balance || 0, { decimals: true })}</TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {new Date(u.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openWallet(u)} data-testid={`wallet-btn-${u.id}`}>
                        <Wallet className="w-3 h-3 mr-1" /> Wallet
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="rounded-full px-2" data-testid={`more-btn-${u.id}`}>
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#0B1120] border-white/10 text-white">
                          <DropdownMenuItem onClick={() => toggleAffiliate(u)} data-testid={`toggle-affiliate-${u.id}`}>
                            {u.is_affiliate ? (
                              <><StarOff className="w-4 h-4 mr-2" /> Disable affiliate</>
                            ) : (
                              <><Star className="w-4 h-4 mr-2 text-[#D4AF37]" /> Make affiliate</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => deleteUser(u)} className="text-red-400 focus:text-red-400" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* WALLET DIALOG */}
      <Dialog open={!!walletUser} onOpenChange={(o) => !o && setWalletUser(null)}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Wallet — {walletUser?.name}</DialogTitle>
          </DialogHeader>
          {!walletData ? (
            <div className="text-gray-500 text-sm py-6">Loading...</div>
          ) : (
            <div className="space-y-5">
              <div className="bp-surface rounded-xl p-5 flex items-end justify-between">
                <div>
                  <div className="overline">Current balance</div>
                  <div className="font-heading text-4xl text-[#D4AF37] mt-1 font-light" data-testid="wallet-balance">
                    {fmt(walletData.wallet_balance, { decimals: true })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{walletData.email}</div>
                </div>
                {!mode && (
                  <div className="flex gap-2">
                    <Button size="sm" className="btn-gold rounded-full" onClick={() => setMode("credit")} data-testid="wallet-credit-btn">
                      <Plus className="w-3 h-3 mr-1" /> Credit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-red-400 border-red-500/40 hover:bg-red-500/10"
                      onClick={() => setMode("debit")}
                      data-testid="wallet-debit-btn"
                    >
                      <Minus className="w-3 h-3 mr-1" /> Debit
                    </Button>
                  </div>
                )}
              </div>

              {mode && (
                <div className="bp-surface rounded-xl p-5 space-y-3">
                  <div className="overline">{mode === "credit" ? "Credit wallet" : "Debit wallet"}</div>
                  <div>
                    <Label className="text-gray-300">Amount (NGN)</Label>
                    <Input
                      data-testid="wallet-amount"
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Note (optional)</Label>
                    <Input
                      data-testid="wallet-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Manual rental payout, refund, bonus..."
                      className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]"
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={() => { setMode(null); setAmount(""); setNote(""); }}>
                      Cancel
                    </Button>
                    <Button
                      disabled={busy}
                      onClick={submitAdjust}
                      className={mode === "credit" ? "btn-gold rounded-full" : "bg-red-500 hover:bg-red-600 rounded-full text-white"}
                      data-testid="wallet-submit"
                    >
                      {busy ? "Processing..." : `Confirm ${mode}`}
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <div className="overline mb-3">Wallet history</div>
                <div className="bp-surface rounded-xl divide-y divide-white/5 max-h-72 overflow-y-auto">
                  {walletData.history.length === 0 ? (
                    <div className="p-6 text-gray-500 text-sm text-center">No wallet activity</div>
                  ) : (
                    walletData.history.map((t) => {
                      const sign = t.amount < 0 ? "-" : "+";
                      const color = t.amount < 0 ? "text-red-400" : "text-[#D4AF37]";
                      return (
                        <div key={t.id} className="p-3 flex items-center justify-between" data-testid={`wallet-tx-${t.id}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-white capitalize">{t.type.replace("_", " ")}</div>
                            <div className="text-[11px] text-gray-500 truncate">{t.note || new Date(t.created_at).toLocaleString()}</div>
                          </div>
                          <div className={`font-mono text-sm ${color} shrink-0 ml-3`}>
                            {sign}{fmt(Math.abs(t.amount), { decimals: true })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalletUser(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KycPill({ status }) {
  const map = {
    none: { c: "#6B7280", label: "Not started" },
    pending: { c: "#F59E0B", label: "Pending" },
    approved: { c: "#10B981", label: "Verified" },
    rejected: { c: "#EF4444", label: "Rejected" },
  };
  const s = map[status] || map.none;
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ color: s.c, background: `${s.c}15`, border: `1px solid ${s.c}40` }}
    >
      {s.label}
    </span>
  );
}
